import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import {
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserCheck,
  FaCalendarAlt,
  FaRoute,
  FaExchangeAlt,
} from "react-icons/fa";
import {
  formatObservationDateTime,
  hasExecutedPeriod,
  hasScheduleExecutionDivergence,
} from "../../helpers/observationDateUtils";

/**
 * Determines if an observation is scheduled (future start_date, not yet confirmed).
 */
export function isObsScheduled(obs) {
  return !obs.confirmed && new Date(obs.start_date) > new Date();
}

/**
 * Determines if an observation is expired (past start_date, not confirmed).
 */
export function isObsExpired(obs) {
  return !obs.confirmed && new Date(obs.start_date) <= new Date();
}

/**
 * Returns the appropriate CSS class for the observation card.
 */
export function getObsCardClass(obs) {
  if (obs.confirmed) return "observation-card observation-card--confirmed";
  if (new Date(obs.start_date) > new Date())
    return "observation-card observation-card--scheduled";
  return "observation-card observation-card--expired";
}

function formatObservationDateTimeLocal(dateStr) {
  return formatObservationDateTime(dateStr);
}

/**
 * Shows scheduled period (read-only) and, when confirmed, actual execution
 * times plus the operator who confirmed on the platform.
 */
export function ObservationExecutionInfo({ obs, t, compact = false }) {
  if (!obs) return null;

  const diverged = hasScheduleExecutionDivergence(obs);
  const executed = hasExecutedPeriod(obs);

  return (
    <div className={`observation-execution-info${compact ? " observation-execution-info--compact" : ""}`}>
      <div className="observation-execution-info__row observation-execution-info__scheduled">
        <div className="observation-execution-info__label">
          <FaCalendarAlt className="observation-execution-info__icon observation-execution-info__icon--scheduled" />
          <small>{t("Período solicitado")}</small>
        </div>
        <div className="observation-execution-info__values">
          {formatObservationDateTimeLocal(obs.start_date)} →{" "}
          {formatObservationDateTimeLocal(obs.end_date)}
        </div>
      </div>

      {obs.confirmed && (
        <div className="observation-execution-info__confirmed">
          <div className="observation-execution-info__row observation-execution-info__actual">
            <div className="observation-execution-info__label">
              <FaRoute className="observation-execution-info__icon observation-execution-info__icon--executed" />
              <small>{t("Execução real")}</small>
            </div>
            <div className="observation-execution-info__values">
              {executed ? (
                <>
                  {formatObservationDateTimeLocal(obs.executed_start_date)} →{" "}
                  {formatObservationDateTimeLocal(obs.executed_end_date)}
                </>
              ) : (
                <span className="observation-execution-info__missing">
                  {t("Período executado não informado")}
                </span>
              )}
            </div>
          </div>

          {diverged && (
            <div className="observation-execution-info__divergence">
              <FaExchangeAlt style={{ marginRight: "6px" }} />
              {t(
                "Horário diferente do solicitado — o rastreamento usa a execução real."
              )}
            </div>
          )}

          {(obs.confirmed_by_name || obs.confirmed_at) && (
            <div className="observation-execution-info__by">
              <FaUserCheck style={{ marginRight: "6px" }} />
              {t("Confirmado por")}:{" "}
              <strong>{obs.confirmed_by_name || t("Operador")}</strong>
              {obs.confirmed_at && (
                <span className="observation-execution-info__at">
                  {" "}
                  ({formatObservationDateTimeLocal(obs.confirmed_at)})
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatCountdown(ms) {
  if (ms <= 0) return { text: "", days: 0, hours: 0, minutes: 0, seconds: 0 };
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${String(minutes).padStart(2, "0")}min`);
  parts.push(`${String(seconds).padStart(2, "0")}s`);

  return { text: parts.join(" "), days, hours, minutes, seconds };
}

/**
 * ScheduledBadge — displays a live countdown timer and progress bar
 * for observations with a future start_date that haven't been confirmed yet.
 *
 * Props:
 *   - obs: observation object { start_date, created, confirmed }
 *   - onConfirm: callback(obsId) to confirm the observation
 *   - t: i18next translate function
 */
function ScheduledBadge({ obs, onConfirm, t }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    // Only tick if still relevant (not confirmed)
    if (obs.confirmed) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [obs.confirmed]);

  // No badge for confirmed observations
  if (obs.confirmed) return null;

  const startDate = new Date(obs.start_date);
  const createdDate = new Date(obs.created);
  const remainingMs = startDate.getTime() - now.getTime();
  const totalMs = startDate.getTime() - createdDate.getTime();
  const isExpired = remainingMs <= 0;
  const elapsedMs = now.getTime() - createdDate.getTime();
  const progressPct =
    totalMs > 0 ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : 0;

  if (isExpired) {
    // Time is up — show confirmation prompt
    return (
      <div className="scheduled-badge scheduled-badge--expired">
        <div className="scheduled-badge__header">
          <FaExclamationTriangle className="scheduled-badge__icon" />
          <span className="scheduled-badge__label">{t("Tempo esgotado!")}</span>
        </div>
        <div className="scheduled-progress-bar">
          <div
            className="scheduled-progress-bar__fill scheduled-progress-bar__fill--expired"
            style={{ width: "100%" }}
          />
        </div>
        <button
          className="btn btn-sm scheduled-confirm-btn"
          onClick={() => onConfirm(obs.id)}
        >
          <FaCheckCircle style={{ marginRight: "5px" }} />
          {t("Confirmar Aplicação")}
        </button>
      </div>
    );
  }

  // Countdown active
  const countdown = formatCountdown(remainingMs);

  return (
    <div className="scheduled-badge">
      <div className="scheduled-badge__header">
        <FaClock className="scheduled-badge__icon" />
        <span className="scheduled-badge__label">
          {t("Aplicação Agendada")}
        </span>
      </div>
      <div className="scheduled-badge__countdown">
        {t("Tempo restante")}: <strong>{countdown.text}</strong>
      </div>
      <div className="scheduled-progress-bar">
        <div
          className="scheduled-progress-bar__fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  );
}

export default withTranslation()(ScheduledBadge);
